import { baseApi } from "@/services/api";
import { store } from "@/app/store";
import type { AppDispatch } from "@/app/store";
import { DELETE_CONFIRM_TOKEN } from "@/features/catalog-lifecycle/catalogLifecycleTypes";
import type {
  CatalogBulkPurgeResponse,
  CatalogPurgePreflight,
} from "@/features/catalog-lifecycle/catalogLifecycleTypes";

// V2 Music Catalog · Record Labels API client.
//
// Backed by kk-admin-sa-music-catalog (Slice #2). Reads use the projection
// table; mutations patch all cached list queries from the canonical mutation
// response, then poll projection GET until the materializer catches up.
// Avoid invalidating list tags on write — projection refetch can race the
// stream and revert rows to stale data until a manual refresh.

export type RecordLabelStatus = "active" | "deleted";

export interface RecordLabel {
  recordLabelId: string;
  name: string;
  nameLower: string;
  proAffiliations: string[];
  status: RecordLabelStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  materializedAt?: string;
  createdBySub?: string;
  updatedBySub?: string;
  createdBySource?: string | null;
  sourceProcessingJobId?: string | null;
  /** Active Songs only (authoritative server count). */
  activeSongCount?: number;
  deletedAt?: string | null;
  deletedBySub?: string | null;
}

export interface ListRecordLabelsArgs {
  status?: RecordLabelStatus | "all";
  nameStartsWith?: string;
  limit?: number;
  pageToken?: string;
}

export interface ListRecordLabelsResponse {
  recordLabels: RecordLabel[];
  pageToken: string | null;
  limit: number;
  status: string;
}

export interface CreateRecordLabelArgs {
  name: string;
  proAffiliations?: string[];
  allowDuplicateName?: boolean;
}

export interface UpdateRecordLabelArgs {
  recordLabelId: string;
  name?: string;
  proAffiliations?: string[];
  allowDuplicateName?: boolean;
}

export interface ArchiveRecordLabelArgs {
  recordLabelId: string;
}

export interface RestoreRecordLabelArgs {
  recordLabelId: string;
}

export interface RestoreRecordLabelResponse {
  recordLabelId: string;
  status: "active";
  restoredAt: string;
  restoredBy?: string;
  /** Archive history is retained on restore — never erased. */
  archivedAt?: string | null;
  archivedBy?: string | null;
  version: number;
}

/**
 * Purge accepts the Song Library's typed `DELETE` token (canonical) and still
 * sends the legacy exact-name fields when a caller supplies them, so older
 * staging validation scripts keep working.
 */
export interface PurgeRecordLabelArgs {
  recordLabelId: string;
  reason: string;
  confirmName?: string;
  confirmRecordLabelId?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

const PROJECTION_SYNC_MAX_ATTEMPTS = 12;
const PROJECTION_SYNC_INTERVAL_MS = 400;

function recordLabelTimestampMs(label: Pick<RecordLabel, "updatedAt">): number {
  const parsed = Date.parse(label.updatedAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Prefer canonical mutation rows over stale projection reads. */
function isRecordLabelAtLeastAsNew(
  candidate: RecordLabel,
  existing: RecordLabel | undefined,
): boolean {
  if (!existing) return true;
  if (candidate.version > existing.version) return true;
  if (candidate.version < existing.version) return false;
  return recordLabelTimestampMs(candidate) >= recordLabelTimestampMs(existing);
}

function patchAllRecordLabelsLists(
  dispatch: AppDispatch,
  mutator: (draft: ListRecordLabelsResponse, listArgs: ListRecordLabelsArgs) => void,
) {
  const cachedArgs = recordLabelsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listRecordLabels",
  );
  for (const args of cachedArgs) {
    const listArgs = (args ?? {}) as ListRecordLabelsArgs;
    dispatch(
      recordLabelsApi.util.updateQueryData("listRecordLabels", args, (draft) => {
        mutator(draft, listArgs);
      }),
    );
  }
}

function applyRecordLabelToListCache(
  draft: ListRecordLabelsResponse,
  label: RecordLabel,
  listArgs: ListRecordLabelsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  const idx = draft.recordLabels.findIndex(
    (row) => row.recordLabelId === label.recordLabelId,
  );
  const existing = idx >= 0 ? draft.recordLabels[idx] : undefined;
  const merged: RecordLabel =
    typeof label.activeSongCount === "number" ||
    !existing ||
    typeof existing.activeSongCount !== "number"
      ? label
      : { ...label, activeSongCount: existing.activeSongCount };

  if (merged.status === "deleted") {
    if (filterStatus === "active") {
      if (idx >= 0) draft.recordLabels.splice(idx, 1);
      return;
    }
    if (filterStatus === "deleted" || filterStatus === "all") {
      if (existing && !isRecordLabelAtLeastAsNew(merged, existing)) return;
      if (idx >= 0) draft.recordLabels[idx] = merged;
      else draft.recordLabels.unshift(merged);
    }
    return;
  }

  if (filterStatus === "deleted") {
    if (idx >= 0) draft.recordLabels.splice(idx, 1);
    return;
  }

  if (existing && !isRecordLabelAtLeastAsNew(merged, existing)) return;
  if (idx >= 0) draft.recordLabels[idx] = merged;
  else draft.recordLabels.unshift(merged);
}

function removeRecordLabelFromListCache(
  draft: ListRecordLabelsResponse,
  recordLabelId: string,
  listArgs: ListRecordLabelsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.recordLabels.findIndex(
      (row) => row.recordLabelId === recordLabelId,
    );
    if (idx >= 0) {
      draft.recordLabels[idx] = {
        ...draft.recordLabels[idx],
        status: "deleted",
      };
    }
    return;
  }
  draft.recordLabels = draft.recordLabels.filter(
    (row) => row.recordLabelId !== recordLabelId,
  );
}

/**
 * Mirror of removeRecordLabelFromListCache for the Archive → Restore
 * direction: drop the row from an Archived-only list, and flip it back to
 * active in the All view. The Active list gains the row once
 * scheduleProjectionUpsert fetches the canonical projection row.
 */
function restoreRecordLabelInListCache(
  draft: ListRecordLabelsResponse,
  recordLabelId: string,
  listArgs: ListRecordLabelsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.recordLabels.findIndex(
      (row) => row.recordLabelId === recordLabelId,
    );
    if (idx >= 0) {
      draft.recordLabels[idx] = {
        ...draft.recordLabels[idx],
        status: "active",
      };
    }
    return;
  }
  if (filterStatus === "deleted") {
    draft.recordLabels = draft.recordLabels.filter(
      (row) => row.recordLabelId !== recordLabelId,
    );
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function scheduleProjectionUpsert(
  dispatch: AppDispatch,
  recordLabelId: string,
  canonical?: RecordLabel,
) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            recordLabelsApi.endpoints.getRecordLabel.initiate(
              { recordLabelId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        if (
          canonical &&
          !isRecordLabelAtLeastAsNew(projected, canonical)
        ) {
          continue;
        }
        patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
          applyRecordLabelToListCache(draft, projected, listArgs);
        });
        return;
      } catch {
        // Projection row not visible yet — list cache keeps the canonical patch.
      }
    }
  })();
}

function scheduleProjectionRemoval(dispatch: AppDispatch, recordLabelId: string) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            recordLabelsApi.endpoints.getRecordLabel.initiate(
              { recordLabelId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        if (projected.status === "deleted") {
          patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
            removeRecordLabelFromListCache(draft, recordLabelId, listArgs);
          });
          return;
        }
      } catch {
        patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
          removeRecordLabelFromListCache(draft, recordLabelId, listArgs);
        });
        return;
      }
    }
  })();
}

export const recordLabelsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listRecordLabels: builder.query<
      ListRecordLabelsResponse,
      ListRecordLabelsArgs | void
    >({
      query: (args) => {
        const params: Record<string, string | number> = {};
        if (args && args.status) params.status = args.status;
        if (args && args.nameStartsWith) params.nameStartsWith = args.nameStartsWith;
        if (args && args.limit) params.limit = args.limit;
        if (args && args.pageToken) params.pageToken = args.pageToken;
        return {
          url: "/record-labels",
          method: "GET",
          params,
        };
      },
      transformResponse: (raw: ApiEnvelope<ListRecordLabelsResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load record labels",
          );
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.recordLabels
          ? [
              ...result.recordLabels.map((row) => ({
                type: "RecordLabels" as const,
                id: row.recordLabelId,
              })),
              { type: "RecordLabels" as const, id: "LIST" },
            ]
          : [{ type: "RecordLabels" as const, id: "LIST" }],
    }),
    getRecordLabel: builder.query<
      RecordLabel,
      { recordLabelId: string; includeDeleted?: boolean }
    >({
      query: ({ recordLabelId, includeDeleted }) => ({
        url: `/record-labels/${encodeURIComponent(recordLabelId)}`,
        method: "GET",
        params: includeDeleted ? { includeDeleted: "true" } : {},
      }),
      transformResponse: (raw: ApiEnvelope<{ recordLabel: RecordLabel }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.recordLabel) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load record label",
          );
        }
        return raw.data.recordLabel;
      },
      providesTags: (_result, _error, { recordLabelId }) => [
        { type: "RecordLabels" as const, id: recordLabelId },
      ],
    }),
    createRecordLabel: builder.mutation<RecordLabel, CreateRecordLabelArgs>({
      query: (body) => ({
        url: "/record-labels",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ recordLabel: RecordLabel }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.recordLabel) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to create record label",
          );
        }
        return raw.data.recordLabel;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
            applyRecordLabelToListCache(draft, created, listArgs);
          });
          scheduleProjectionUpsert(dispatch, created.recordLabelId, created);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    updateRecordLabel: builder.mutation<RecordLabel, UpdateRecordLabelArgs>({
      query: ({ recordLabelId, ...patch }) => ({
        url: `/record-labels/${encodeURIComponent(recordLabelId)}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: (raw: ApiEnvelope<{ recordLabel: RecordLabel }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.recordLabel) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to update record label",
          );
        }
        return raw.data.recordLabel;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
            applyRecordLabelToListCache(draft, updated, listArgs);
          });
          scheduleProjectionUpsert(dispatch, updated.recordLabelId, updated);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    archiveRecordLabel: builder.mutation<
      {
        recordLabelId: string;
        status: RecordLabelStatus;
        deletedAt: string;
        version: number;
      },
      ArchiveRecordLabelArgs
    >({
      query: ({ recordLabelId }) => ({
        url: `/record-labels/${encodeURIComponent(recordLabelId)}`,
        method: "DELETE",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          recordLabelId: string;
          status: RecordLabelStatus;
          deletedAt: string;
          version: number;
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to archive record label",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ recordLabelId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
            removeRecordLabelFromListCache(draft, recordLabelId, listArgs);
          });
          scheduleProjectionRemoval(dispatch, recordLabelId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    restoreRecordLabel: builder.mutation<
      RestoreRecordLabelResponse,
      RestoreRecordLabelArgs
    >({
      query: ({ recordLabelId }) => ({
        url: `/record-labels/${encodeURIComponent(recordLabelId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreRecordLabelResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to restore record label",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ recordLabelId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllRecordLabelsLists(dispatch, (draft, listArgs) => {
            restoreRecordLabelInListCache(draft, recordLabelId, listArgs);
          });
          scheduleProjectionUpsert(dispatch, recordLabelId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    preflightPurgeRecordLabel: builder.mutation<
      CatalogPurgePreflight,
      { recordLabelId: string }
    >({
      query: ({ recordLabelId }) => ({
        url: `/record-labels/${encodeURIComponent(
          recordLabelId,
        )}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogPurgePreflight>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load record label deletion preflight",
          );
        }
        return raw.data;
      },
      // Dry run — nothing to invalidate or patch.
      invalidatesTags: () => [],
    }),
    bulkPurgeRecordLabels: builder.mutation<
      CatalogBulkPurgeResponse,
      { ids: string[]; reason: string }
    >({
      query: ({ ids, reason }) => ({
        url: "/record-labels/permanent-delete",
        method: "POST",
        body: { ids, confirm: DELETE_CONFIRM_TOKEN, reason },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogBulkPurgeResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to permanently delete record labels",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const purgedIds = (data.results || [])
            .filter((row) => row.ok)
            .map((row) => row.id);
          if (purgedIds.length === 0) return;
          patchAllRecordLabelsLists(dispatch, (draft) => {
            draft.recordLabels = draft.recordLabels.filter(
              (row) => !purgedIds.includes(row.recordLabelId),
            );
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    purgeRecordLabel: builder.mutation<
      {
        recordLabelId: string;
        name?: string;
        purged: boolean;
        dependencyCheck?: {
          checkStatus: string;
          associatedTrackCount: number;
          message?: string;
        };
      },
      PurgeRecordLabelArgs
    >({
      query: ({ recordLabelId, confirmName, confirmRecordLabelId, reason }) => ({
        url: `/record-labels/${encodeURIComponent(recordLabelId)}/purge`,
        method: "POST",
        body: {
          confirm: DELETE_CONFIRM_TOKEN,
          reason,
          ...(confirmName ? { confirmName } : {}),
          ...(confirmRecordLabelId ? { confirmRecordLabelId } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          recordLabelId: string;
          name?: string;
          purged: boolean;
          dependencyCheck?: {
            checkStatus: string;
            associatedTrackCount: number;
            message?: string;
          };
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to purge record label",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ recordLabelId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Hard purge — remove from every cached list (unlike soft archive,
          // which keeps the row on the All tab as status=deleted).
          patchAllRecordLabelsLists(dispatch, (draft) => {
            draft.recordLabels = draft.recordLabels.filter(
              (row) => row.recordLabelId !== recordLabelId,
            );
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
  }),
});

export const {
  useListRecordLabelsQuery,
  useGetRecordLabelQuery,
  useCreateRecordLabelMutation,
  useUpdateRecordLabelMutation,
  useArchiveRecordLabelMutation,
  useRestoreRecordLabelMutation,
  usePreflightPurgeRecordLabelMutation,
  useBulkPurgeRecordLabelsMutation,
  usePurgeRecordLabelMutation,
} = recordLabelsApi;

/** True when an RTK Query error is an HTTP 409 duplicate-name conflict. */
export function isDuplicateNameConflict(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const anyErr = err as Record<string, unknown>;
  if (anyErr.status === 409 || anyErr.status === "409") return true;
  const data = anyErr.data as Record<string, unknown> | undefined;
  if (data && typeof data.message === "string") {
    return data.message.toLowerCase().includes("already exists");
  }
  const nested = anyErr.error as Record<string, unknown> | undefined;
  if (nested) {
    if (nested.status === 409 || nested.status === "409") return true;
    const nestedData = nested.data as Record<string, unknown> | undefined;
    if (nestedData && typeof nestedData.message === "string") {
      return nestedData.message.toLowerCase().includes("already exists");
    }
  }
  return false;
}

export function isCatalogDependencyConflict(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const anyErr = err as Record<string, unknown>;
  if (anyErr.status !== 409 && anyErr.status !== "409") return false;
  const data = anyErr.data as Record<string, unknown> | undefined;
  if (data && typeof data.details === "object" && data.details) {
    const details = data.details as Record<string, unknown>;
    return typeof details.associatedTrackCount === "number";
  }
  const message =
    (typeof data?.message === "string" && data.message) ||
    (typeof anyErr.message === "string" && anyErr.message) ||
    "";
  return message.toLowerCase().includes("associated with");
}

export function extractRecordLabelErrorMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (
      data &&
      typeof data === "object" &&
      typeof data.message === "string" &&
      data.message.length > 0
    ) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}
